import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    X, 
    Users, 
    Code, 
    Palette, 
    Rocket,
    Send,
    Loader2,
    CheckCircle,
    Mail
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HELP_TYPES = [
    { 
        id: 'developer', 
        label: 'Developer', 
        description: 'Someone to help build the technical side',
        icon: Code,
        color: 'blue'
    },
    { 
        id: 'designer', 
        label: 'Designer', 
        description: 'Someone to help with UI/UX design',
        icon: Palette,
        color: 'pink'
    },
    { 
        id: 'cofounder', 
        label: 'Co-founder', 
        description: 'A partner to build this together',
        icon: Rocket,
        color: 'violet'
    }
];

export const CollaborationModal = ({ 
    isOpen, 
    onClose, 
    projectId = null,
    ideaSummary = ''
}) => {
    const { isAuthenticated, getAuthHeaders } = useAuth();
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [contact, setContact] = useState('');
    const [description, setDescription] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleTypeToggle = (typeId) => {
        setSelectedTypes(prev => 
            prev.includes(typeId) 
                ? prev.filter(t => t !== typeId)
                : [...prev, typeId]
        );
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!isAuthenticated) {
            toast.error('Please sign in to submit a collaboration request');
            return;
        }
        
        if (selectedTypes.length === 0) {
            toast.error('Please select at least one type of help');
            return;
        }
        
        if (!contact.trim()) {
            toast.error('Please provide your contact info');
            return;
        }

        setIsSubmitting(true);
        
        try {
            await axios.post(
                `${BACKEND_URL}/api/collaboration-requests`,
                {
                    project_id: projectId,
                    idea_summary: ideaSummary || 'No summary provided',
                    help_type: selectedTypes,
                    contact: contact.trim(),
                    description: description.trim() || null
                },
                { headers: getAuthHeaders() }
            );
            
            setIsSubmitted(true);
            toast.success('Collaboration request submitted!');
            
            // Reset and close after a moment
            setTimeout(() => {
                onClose();
                setIsSubmitted(false);
                setSelectedTypes([]);
                setContact('');
                setDescription('');
            }, 2000);
        } catch (error) {
            console.error('Failed to submit collaboration request:', error);
            toast.error('Failed to submit request. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    className="glass-card w-full max-w-lg overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                    data-testid="collaboration-modal"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-5 border-b border-white/10">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-violet-500/20 to-pink-500/20">
                                <Users className="w-5 h-5 text-violet-400" />
                            </div>
                            <div>
                                <h3 className="font-heading font-semibold text-lg">
                                    🤝 Collaborate on this idea
                                </h3>
                                <p className="text-xs text-gray-500">
                                    Find the right people to help build
                                </p>
                            </div>
                        </div>
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onClose}
                            className="hover:bg-white/10"
                        >
                            <X className="w-5 h-5" />
                        </Button>
                    </div>

                    {/* Success State */}
                    {isSubmitted ? (
                        <div className="p-8 text-center">
                            <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-green-400" />
                            </div>
                            <h4 className="font-semibold text-white text-lg mb-2">
                                Request Submitted!
                            </h4>
                            <p className="text-sm text-gray-400">
                                We'll notify you when we launch our collaboration matching feature.
                            </p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit}>
                            {/* Help Type Selection */}
                            <div className="p-5 border-b border-white/10">
                                <p className="text-sm text-gray-400 mb-3">
                                    What kind of help are you looking for?
                                </p>
                                <div className="space-y-2">
                                    {HELP_TYPES.map((type) => {
                                        const Icon = type.icon;
                                        const isSelected = selectedTypes.includes(type.id);
                                        return (
                                            <button
                                                key={type.id}
                                                type="button"
                                                onClick={() => handleTypeToggle(type.id)}
                                                className={`w-full p-3 rounded-lg border text-left transition-all ${
                                                    isSelected 
                                                        ? `border-${type.color}-500/50 bg-${type.color}-500/10` 
                                                        : 'border-white/10 bg-white/5 hover:bg-white/10'
                                                }`}
                                                data-testid={`help-type-${type.id}`}
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-2 rounded-lg ${
                                                        isSelected 
                                                            ? `bg-${type.color}-500/20` 
                                                            : 'bg-white/10'
                                                    }`}>
                                                        <Icon className={`w-4 h-4 ${
                                                            isSelected 
                                                                ? `text-${type.color}-400` 
                                                                : 'text-gray-400'
                                                        }`} />
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className={`font-medium ${
                                                            isSelected ? 'text-white' : 'text-gray-300'
                                                        }`}>
                                                            {type.label}
                                                        </p>
                                                        <p className="text-xs text-gray-500">
                                                            {type.description}
                                                        </p>
                                                    </div>
                                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                                                        isSelected 
                                                            ? `border-${type.color}-400 bg-${type.color}-400` 
                                                            : 'border-gray-600'
                                                    }`}>
                                                        {isSelected && (
                                                            <CheckCircle className="w-3 h-3 text-black" />
                                                        )}
                                                    </div>
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Contact & Description */}
                            <div className="p-5 space-y-4">
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1.5">
                                        <Mail className="w-3 h-3 inline mr-1" />
                                        How can collaborators reach you?
                                    </label>
                                    <input
                                        type="text"
                                        value={contact}
                                        onChange={(e) => setContact(e.target.value)}
                                        placeholder="Email, Twitter handle, or Discord username"
                                        className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-gray-600 focus:border-violet-500/50 focus:outline-none transition-colors"
                                        data-testid="collaboration-contact"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm text-gray-400 mb-1.5">
                                        What are you looking for? (optional)
                                    </label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="Describe what skills or experience you need..."
                                        rows={3}
                                        className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-gray-600 focus:border-violet-500/50 focus:outline-none transition-colors resize-none"
                                        data-testid="collaboration-description"
                                    />
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="p-5 border-t border-white/10">
                                <Button
                                    type="submit"
                                    disabled={isSubmitting || selectedTypes.length === 0 || !contact.trim()}
                                    className="w-full bg-gradient-to-r from-violet-600 to-pink-600 hover:from-violet-500 hover:to-pink-500 text-white rounded-xl py-3"
                                    data-testid="submit-collaboration"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4 mr-2" />
                                            Submit Request
                                        </>
                                    )}
                                </Button>
                                <p className="text-xs text-gray-600 text-center mt-3">
                                    We're building a collaboration network. Early requests help shape it!
                                </p>
                            </div>
                        </form>
                    )}
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};
